import Link from "next/link"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"

import LumenLogo from "@/components/icons/LumenLogo"
import LumenLogoMark from "@/components/icons/LumenLogoMark"
import UserGradientCircle from "@/components/icons/UserGradientCircle"
import { flaskUrl, url } from "@/lib/url"
import { useCurrentUserQuery } from "@/v2/graphql/public-generated-rq"
import { useOrgSlug } from "@/v2/hooks/router"
import { Button, DropdownMenu, Spinner, Truncate } from "@lumen-energy/design-system"
import {
  IconChevronsLeft,
  IconDotsVertical,
  IconLogout,
  IconMenu2,
} from "@tabler/icons-react"

import { PropertySearch } from "../../components/property/PropertySearch"

export interface HeaderProps {
  onToggleSidebar: () => void
  isSidebarOpen: boolean
}

export const Header = ({ onToggleSidebar, isSidebarOpen }: HeaderProps) => {
  const router = useRouter()
  const orgSlug = useOrgSlug()

  const isInvestRoute = router.pathname.startsWith("/[orgSlug]/invest")

  // Placeholder text for PropertySearch changes depending on viewport width
  const targetWidth = 900
  const [isViewportWide, setIsViewportWide] = useState<boolean>(
    window.innerWidth >= targetWidth,
  )
  const updateWidth = () => setIsViewportWide(window.innerWidth >= targetWidth)
  useEffect(() => {
    window.addEventListener("resize", updateWidth)
    return () => window.removeEventListener("resize", updateWidth)
  })

  return (
    <header className="sticky top-0 isolate z-30 flex h-header w-full items-center justify-between bg-brand-primary-900 p-4 print:hidden">
      <div className="flex w-full flex-nowrap items-center justify-between gap-5">
        <div className="flex gap-2">
          <Button
            aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
            className="text-white"
            icon={isSidebarOpen ? IconChevronsLeft : IconMenu2}
            iconOnly
            noBorder
            variant="neutral"
            size="xlarge"
            onClick={onToggleSidebar}
          />
          <Link href={url(["/[orgSlug]", { orgSlug }])}>
            <LumenLogo className="hidden h-9 w-auto cursor-pointer md:block" dark />
            <LumenLogoMark className="h-9 w-auto cursor-pointer md:hidden" />
          </Link>
        </div>
        {!isInvestRoute ? (
          <PropertySearch
            orgSlug={orgSlug}
            className="max-w-[600px]"
            placeholder={
              isViewportWide
                ? "Search to jump to a specific property"
                : "Search properties"
            }
            dark
            showLoadingSpinner
          />
        ) : null}

        <HeaderUserDropdown />
      </div>
    </header>
  )
}

function HeaderUserDropdown() {
  const { data, isLoading } = useCurrentUserQuery()

  if (isLoading || !data) {
    return (
      <div className="flex w-[265px] justify-end">
        <Spinner size={28} className="inline text-white" />
      </div>
    )
  }

  return (
    <DropdownMenu.Root>
      <div className="flex max-w-[265px] items-center gap-3 overflow-hidden text-white">
        <div className="hidden w-[40px] md:block">
          <UserGradientCircle />
        </div>
        <div className="hidden min-w-0 grow md:block">
          <div className="text-body1">
            <Truncate
              text={data.currentUser.name || data.currentUser.email || "Guest"}
              disableTooltip
            />
          </div>
          {data.currentUser.name && data.currentUser.email && (
            <div className="text-body2">
              <Truncate text={data.currentUser.email} disableTooltip />
            </div>
          )}
        </div>
        <DropdownMenu.Trigger asChild>
          <div className="box-content h-6 w-6 cursor-pointer rounded-full p-1 hover:bg-brand-primary-700 aria-expanded:bg-brand-primary-700">
            <IconDotsVertical size={24} />
            <span className="sr-only">Switch view or sign out</span>
          </div>
        </DropdownMenu.Trigger>
      </div>

      <DropdownMenu.Content
        align="end"
        side="bottom"
        className="max-w-xs"
        sideOffset={16}
      >
        <DropdownMenu.Label>
          You are signed in as {data.currentUser.email}
        </DropdownMenu.Label>
        <DropdownMenu.Item asChild>
          <a href={flaskUrl(["/logout"])}>
            <IconLogout />
            Sign out
          </a>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
